import React, { useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Megaphone, Sparkles, Copy, Check } from "lucide-react";

export function BroadcastTab() {
  const [form, setForm] = useState({ subject: "", body_html: "", audience: "newsletter" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  React.useEffect(() => { api.get("/admin/broadcasts").then((r) => setHistory(r.data.items)); }, [result]);

  const send = async () => {
    if (!form.subject || !form.body_html) return toast.error("Subject and body are required");
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", form);
      setResult(data);
      toast.success(`Broadcast sent to ${data.sent} devotees`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="broadcast-tab" className="grid lg:grid-cols-5 gap-7">
      <div className="lg:col-span-3 bg-[#FFFDF9] sacred-border rounded-2xl p-7">
        <div className="flex items-center gap-3 mb-5">
          <Megaphone className="w-6 h-6 text-[#E65100]" />
          <h3 className="font-serif text-2xl text-[#4A0E17]">Send a broadcast</h3>
        </div>
        <p className="text-sm text-[#63534E] mb-5">One-tap email campaign to your sangha. Wrapped in the sacred Pancham Dham template automatically.</p>

        <div className="space-y-4">
          <div>
            <label className="overline">Audience</label>
            <div className="mt-2 flex gap-2">
              {[{ k: "newsletter", l: "Newsletter subscribers" }, { k: "seva_club", l: "Seva Club members" }].map((o) => (
                <button key={o.k} data-testid={`audience-${o.k}`} onClick={() => setForm({ ...form, audience: o.k })}
                  className={`px-4 h-10 rounded-full text-sm transition-sacred border ${form.audience === o.k ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-white text-[#4A0E17] border-[#D4AF37]/40"}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <Input data-testid="broadcast-subject" placeholder="Subject line…" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Textarea data-testid="broadcast-body" placeholder="<p>Dear devotees,</p>&#10;<p>May this month bring peace…</p>&#10;Use simple HTML paragraphs. Links auto-filtered." rows={10} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} className="bg-white border-[#D4AF37]/40 font-mono text-sm" />
          <Button data-testid="broadcast-send" disabled={sending} onClick={send} className="bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-11 px-7">
            {sending ? "Sending…" : "Send to sangha"}
          </Button>
        </div>

        {result && (
          <div data-testid="broadcast-result" className="mt-6 bg-[#F3E5AB]/40 border border-[#D4AF37]/40 rounded-xl p-4 text-sm text-[#4A0E17]">
            Sent: <strong>{result.sent}</strong> · Failed: <strong>{result.failed}</strong> · Total: <strong>{result.total}</strong>
          </div>
        )}
      </div>

      <div className="lg:col-span-2 bg-[#FFFDF9] sacred-border rounded-2xl p-7">
        <h3 className="font-serif text-xl text-[#4A0E17]">Recent broadcasts</h3>
        <div className="mt-4 space-y-3 max-h-[500px] overflow-y-auto">
          {history.length === 0 && <div className="text-sm text-[#63534E]">None yet.</div>}
          {history.map((b) => (
            <div key={b.id} className="border border-[#D4AF37]/30 rounded-xl p-3">
              <div className="font-semibold text-[#4A0E17] text-sm">{b.subject}</div>
              <div className="text-xs text-[#63534E] mt-1 font-mono">
                {new Date(b.created_at).toLocaleString()} · {b.audience} · {b.sent}✓ / {b.failed}✕
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AIPostsTab() {
  const [occasion, setOccasion] = useState("Maha Shivratri — the great night of Shiva");
  const [platforms, setPlatforms] = useState(["instagram", "twitter", "facebook"]);
  const [tone, setTone] = useState("devotional");
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState(null);
  const [copied, setCopied] = useState(null);

  const generate = async () => {
    setLoading(true);
    setDrafts(null);
    try {
      const { data } = await api.post("/admin/ai/social-post", { occasion, platforms, tone });
      setDrafts(data.drafts);
      toast.success("Drafts generated — review and copy to your social accounts");
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  const copy = (text, k) => {
    navigator.clipboard.writeText(text);
    setCopied(k);
    setTimeout(() => setCopied(null), 1500);
    toast.success("Copied to clipboard");
  };

  const togglePlatform = (p) => {
    setPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  };

  return (
    <div data-testid="ai-posts-tab" className="grid lg:grid-cols-5 gap-7">
      <div className="lg:col-span-2 bg-[#FFFDF9] sacred-border rounded-2xl p-7 h-fit">
        <div className="flex items-center gap-3 mb-5">
          <Sparkles className="w-6 h-6 text-[#E65100]" />
          <h3 className="font-serif text-2xl text-[#4A0E17]">AI Social Drafts</h3>
        </div>
        <p className="text-sm text-[#63534E] mb-5">GPT-5.2 drafts platform-ready copy. You review, then paste into Instagram/Twitter/Facebook.</p>

        <div className="space-y-4">
          <Textarea data-testid="ai-occasion" placeholder="e.g. Shivratri, Pradosh, Monday aarti…" rows={2} value={occasion} onChange={(e) => setOccasion(e.target.value)} className="bg-white border-[#D4AF37]/40" />
          <div>
            <label className="overline">Platforms</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {["instagram", "twitter", "facebook", "linkedin"].map((p) => (
                <button key={p} data-testid={`platform-${p}`} onClick={() => togglePlatform(p)}
                  className={`px-3 h-9 rounded-full text-xs capitalize transition-sacred border ${platforms.includes(p) ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-white text-[#4A0E17] border-[#D4AF37]/40"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="overline">Tone</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {["devotional", "poetic", "informative", "celebratory"].map((t) => (
                <button key={t} onClick={() => setTone(t)}
                  className={`px-3 h-9 rounded-full text-xs capitalize transition-sacred border ${tone === t ? "bg-[#E65100] text-white border-[#E65100]" : "bg-white text-[#4A0E17] border-[#D4AF37]/40"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <Button data-testid="ai-generate" disabled={loading || platforms.length === 0} onClick={generate} className="w-full bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-11">
            {loading ? "Generating…" : "✨ Draft posts"}
          </Button>
        </div>
      </div>

      <div className="lg:col-span-3 space-y-4">
        {!drafts && !loading && (
          <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-10 text-center text-[#63534E]">
            Your drafts will appear here. Edit before posting.
          </div>
        )}
        {loading && <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-10 text-center text-[#63534E]">Composing devotional copy… 🕉</div>}
        {drafts && Object.entries(drafts).map(([platform, text]) => (
          <div key={platform} data-testid={`draft-${platform}`} className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="overline capitalize">{platform}</div>
              <button data-testid={`copy-${platform}`} onClick={() => copy(String(text), platform)} className="btn-ghost-gold text-xs h-9 px-4">
                {copied === platform ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
            </div>
            <pre className="whitespace-pre-wrap font-sans text-[#1F1412] leading-relaxed">{String(text)}</pre>
          </div>
        ))}
      </div>
    </div>
  );
}
